import { AnnotationPlane } from '~/domain/annotation/annotation';
import {
    euclideanDistanceMm,
    PointMm,
} from '~/domain/geometry/patientGeometry';
import {
    createLineProfileId,
    LineProfile,
    LineProfileDraft,
    lineProfileInFrame,
    MIN_PROFILE_LENGTH_MM,
} from '~/domain/intensity/lineProfile';
import type { ShownPaneImage } from '~/engine/view/paneImage';
import {
    isPointInsideDisplayedImage,
    paneToImageFrame,
    pinToViewPlane,
    viewPlaneCoordinateMm,
} from '~/engine/view/planeViewGeometry';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { AnnotationPlacementTool } from './AnnotationPlacementTool';

/** The placement in the pane's frame, with the pane it was started on. */
interface ActiveDraft {
    readonly pane: ShownPaneImage;
    readonly plane: AnnotationPlane;
    readonly startMm: PointMm;
    endMm: PointMm | null;
}

/**
 * Engine tool for placing a line profile: first click drops the start, the
 * cursor rubber-bands the end, the second click commits and returns to idle.
 * Commit stores geometry only; intensities are re-sampled on demand.
 *
 * Both endpoints must fall inside the image the pane shows, so a profile can
 * never be placed over voxels that do not exist. They are stored in that
 * image's own frame, where the panel samples it. The lifecycle itself lives
 * in {@link AnnotationPlacementTool}.
 */
export class LineProfileTool extends AnnotationPlacementTool {
    public readonly toolType = 'lineProfile';

    private readonly _onDraftChanged: (draft: LineProfileDraft | null) => void;
    private readonly _onCommit: (profile: LineProfile) => void;

    private _draft: ActiveDraft | null = null;

    public constructor(
        onDraftChanged: (draft: LineProfileDraft | null) => void,
        onCommit: (profile: LineProfile) => void
    ) {
        super();
        this._onDraftChanged = onDraftChanged;
        this._onCommit = onCommit;
    }

    protected override startPlacement(
        view: PlaneView,
        plane: AnnotationPlane,
        pointMm: PointMm,
        pane: ShownPaneImage
    ): boolean {
        if (!isPointInsideDisplayedImage(pointMm, view)) {
            return false;
        }
        this._draft = { pane, plane, startMm: pointMm, endMm: null };
        this.emitDraft();
        return true;
    }

    protected override placePoint(view: PlaneView, pointMm: PointMm): void {
        const draft = this._draft;
        if (!draft) {
            return;
        }
        const endMm = pinToPlaneOf(view, pointMm, draft.startMm);
        // Ignore a click outside the image, and a second click on the start
        // point (a mis-click, not a request for a zero-length profile).
        if (
            !isPointInsideDisplayedImage(endMm, view) ||
            euclideanDistanceMm(draft.startMm, endMm) < MIN_PROFILE_LENGTH_MM
        ) {
            return;
        }
        this._draft = null;
        this.finishPlacement();
        this._onDraftChanged(null);
        this._onCommit(
            lineProfileInFrame(
                {
                    id: createLineProfileId(),
                    plane: draft.plane,
                    startMm: draft.startMm,
                    endMm,
                    visible: true,
                },
                paneToImageFrame(draft.pane)
            )
        );
    }

    protected override moveCursor(view: PlaneView, pointMm: PointMm): void {
        if (!this._draft) {
            return;
        }
        this._draft.endMm = pinToPlaneOf(view, pointMm, this._draft.startMm);
        this.emitDraft();
    }

    protected override undoPoint(): void {
        this.cancelDraft();
    }

    protected override lastPlacedPoint(): PointMm | null {
        return this._draft?.startMm ?? null;
    }

    protected override discardDraft(): void {
        this._draft = null;
        this._onDraftChanged(null);
    }

    private emitDraft(): void {
        if (!this._draft) {
            return;
        }
        this._onDraftChanged(
            lineProfileInFrame(
                {
                    plane: this._draft.plane,
                    startMm: this._draft.startMm,
                    endMm: this._draft.endMm,
                },
                paneToImageFrame(this._draft.pane)
            )
        );
    }
}

/**
 * Cursor point pinned to the plane the draft started on, so scrolling between
 * the two clicks cannot produce a cross-slice line (single-slice by design).
 */
function pinToPlaneOf(
    view: PlaneView,
    pointMm: PointMm,
    anchorMm: PointMm
): PointMm {
    return pinToViewPlane(pointMm, view, viewPlaneCoordinateMm(anchorMm, view));
}
